import {
    createTableColumn,
    TableCellLayout,
    Tooltip,
    Checkbox,
    Spinner,
    Button,
    mergeClasses,
    TableColumnDefinition,
    Badge,
} from '@fluentui/react-components';
import {
    NumberRow16Regular,
    Status16Regular,
    Tag16Regular,
    CalendarClock16Regular,
    Airplane16Regular,
    Eye16Regular,
    EyeOff16Regular,
    ArrowRoutingRegular,
    Person16Regular,
    Timer16Regular,
    Hourglass16Regular,
    PauseCircle16Regular,
    PlayCircle16Regular,
    People16Regular,
    Chat16Regular,
    Notepad16Regular,
    CommentNote20Regular,
} from '@fluentui/react-icons';
import { TFunction } from 'i18next';
import { ActiveConversationRow } from '../../../model/brokerConnections/ActiveConversationRow';
import { useActiveConversationsTableStyles } from './ActiveConversationsTable.styles';
import {
    formatCompactDateTime,
} from '../../../utils/formatters';
import {
    safeLocaleCompare,
    safeDateCompare,
    safeFlightCompare,
    isOpenForHour,
} from '../../../utils/activeConversationsUtils';

export const baseColumnSizingOptions = {
    select: {
        defaultWidth: 215,
        minWidth: 215,
    },
    elapsedTime: {
        defaultWidth: 140,
        minWidth: 120,
    },
    handlingTime: {
        defaultWidth: 160,
        minWidth: 140,
    },
    lastMessage: {
        defaultWidth: 150,
        minWidth: 130,
    },
    flight: {
        defaultWidth: 120,
        minWidth: 100,
    },
    arrivalTime: {
        defaultWidth: 140,
        minWidth: 120,
    },
    route: {
        defaultWidth: 120,
        minWidth: 100,
    },
    acReg: {
        defaultWidth: 140,
        minWidth: 120,
    },
    read: {
        defaultWidth: 80,
        minWidth: 60,
    },
    initiatedFrom: {
        defaultWidth: 150,
        minWidth: 130,
    },
    status: {
        defaultWidth: 200,
        minWidth: 160,
    },
    categories: {
        defaultWidth: 480,
        minWidth: 360,
    },
    groundTeamOwner: {
        defaultWidth: 200,
        minWidth: 160,
    },
};

export const getColumnSizingOptions = (groupByStatus: boolean) => ({
    ...baseColumnSizingOptions,
    select: {
        defaultWidth: groupByStatus ? 215 : 200,
        minWidth: groupByStatus ? 215 : 180,
    },
});

export const getHeaderCellClassName = (
    columnId: string,
    styles: ReturnType<typeof useActiveConversationsTableStyles>
): string => {
    if (columnId.endsWith('Action')) {
        return styles.actionCell;
    }
    if (columnId === 'select') {
        return styles.tableSelectionCell;
    }
    return '';
};

export const getCellClassName = (
    columnId: string,
    styles: ReturnType<typeof useActiveConversationsTableStyles>
): string => {
    if (columnId === 'select') {
        return styles.tableSelectionCell;
    }
    if (columnId === 'categories') {
        return styles.categoriesCellWrapper;
    }
    return '';
};

export const getColumns = (
    t: TFunction<'translation', undefined>,
    onOpenLink: (m: ActiveConversationRow) => void,
    selected: string[],
    onToggle: (id: string) => void,
    styles: ReturnType<typeof useActiveConversationsTableStyles>,
    resolvingIds: string[],
    markingAsResolvedId: string | null,
    handleClaimConversationClick: (
        conversationId: string,
        currentOwnerId: string | null
    ) => void,
    handleConversationNotesClick: (conversationId: string) => void,
    currentUserId: string | undefined
): TableColumnDefinition<ActiveConversationRow>[] => {
    const getAwaitingGroundResponseClass = (
        item: ActiveConversationRow
    ): string => {
        return item.awaitingGroundResponse ? styles.awaitingGroundResponse : '';
    };

    const getLastMessageTooltip = (item: ActiveConversationRow): string => {
        if (item.awaitingGroundResponse) {
            return t(
                'Tooltip_LastMessageFromFlightTeam',
                'Last message from the flight team'
            );
        }

        if (item.isAwaitingCrewResponse) {
            return t(
                'Tooltip_LastMessageFromGroundTeam',
                'Last message from the ground team'
            );
        }

        return t(
            'Tooltip_LastMessageSenderUnavailable',
            'Last message sender unavailable'
        );
    };

    const isResolving = (item: ActiveConversationRow): boolean => {
        return (
            resolvingIds.includes(item.conversationId) ||
            item.conversationId === markingAsResolvedId
        );
    };

    return [
        createTableColumn<ActiveConversationRow>({
            columnId: 'select',
            renderCell: (item) => (
                <div className={styles.selectCellWrapper}>
                    {isResolving(item) ? (
                        <Spinner
                            size="tiny"
                            className={styles.spinnerWrapper}
                        />
                    ) : (
                        <Checkbox
                            role="checkbox"
                            shape="circular"
                            data-stop-row-click
                            checked={
                                selected.indexOf(item.conversationId) !== -1
                            }
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                e.stopPropagation();
                                onToggle(item.conversationId);
                            }}
                            className={styles.checkboxWrapper}
                        />
                    )}
                    {!item.isAutoConnection && (
                        <Tooltip
                            content={`${item.conversationNoteCount} ${t(
                                item.conversationNoteCount !== 1
                                    ? 'ActiveConversations_ConversationNotes'
                                    : 'ActiveConversations_ConversationNote'
                            )}`}
                            relationship="label"
                        >
                            <Button
                                className={styles.notesBtn}
                                appearance="primary"
                                icon={<Notepad16Regular />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleConversationNotesClick(
                                        item.conversationId
                                    );
                                }}
                            >
                                {item.conversationNoteCount.toString()}
                            </Button>
                        </Tooltip>
                    )}
                    {item.hasRecentNote && (
                        <Tooltip
                            content={t('ActiveConversations_NewNote', {
                                defaultValue:
                                    'New note added in past {mins} mins',
                            })}
                            relationship="label"
                        >
                            <Badge
                                className={styles.newNoteBadge}
                                icon={<CommentNote20Regular />}
                            />
                        </Tooltip>
                    )}
                    {item.isNew && (
                        <Tooltip
                            content={t('ActiveConversations_NewTooltip', {
                                defaultValue: 'New conversation in past 5 mins',
                            })}
                            relationship="label"
                        >
                            <span
                                data-stop-row-click
                                className={styles.newBadge}
                            >
                                {t('ActiveConversations_NewFlag')}
                            </span>
                        </Tooltip>
                    )}
                </div>
            ),
            renderHeaderCell: () => <></>,
            compare: () => 0,
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'elapsedTime',
            renderCell: (item) => (
                <Tooltip
                    content={`${t('TableHeader_Created')} ${item.dateTime}`}
                    relationship="label"
                >
                    <TableCellLayout
                        className={mergeClasses(
                            getAwaitingGroundResponseClass(item),
                            !item.isAutoConnection &&
                                !item.isAwaitingCrewResponse &&
                                isOpenForHour(item.dateTimeRaw)
                                ? styles.openForHourField
                                : ''
                        )}
                    >
                        {item.isAutoConnection ? '' : item.elapsedTime.toString()}
                    </TableCellLayout>
                </Tooltip>
            ),
            renderHeaderCell: () => {
                return (
                    <Tooltip
                        content={t('Tooltip_ElapsedTime')}
                        relationship="label"
                    >
                        <TableCellLayout media={<Timer16Regular />}>
                            {t('TableHeader_ElapsedTime')}
                        </TableCellLayout>
                    </Tooltip>
                );
            },
            compare: (a, b) => a.elapsedMs - b.elapsedMs,
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'handlingTime',
            renderCell: (item) => {
                const isPaused = item.isPauseStatus && !item.isAutoConnection;
                const isActive =
                    !isPaused && item.handlingTime && !item.isAutoConnection;
                const tooltipContent = isPaused && item.handlingTime
                    ? t('Tooltip_HandlingTimePaused') || 'Timer paused'
                    : isActive
                        ? t('Tooltip_HandlingTimeActive') || 'Timer active'
                        : undefined;

                const cellContent = (
                    <TableCellLayout
                        className={getAwaitingGroundResponseClass(item)}
                    >
                        <div className={styles.statusWrapper}>
                            {isPaused && item.handlingTime && (
                                <PauseCircle16Regular
                                    className={styles.statusIconPaused}
                                />
                            )}
                            {isActive && (
                                <PlayCircle16Regular
                                    className={styles.statusIconActive}
                                />
                            )}
                            {item.isAutoConnection ? '' : item.handlingTime}
                        </div>
                    </TableCellLayout>
                );

                return tooltipContent ? (
                    <Tooltip content={tooltipContent} relationship="label">
                        {cellContent}
                    </Tooltip>
                ) : (
                    cellContent
                );
            },
            renderHeaderCell: () => {
                return (
                    <Tooltip
                        content={t('Tooltip_HandlingTime')}
                        relationship="label"
                    >
                        <TableCellLayout media={<Hourglass16Regular />}>
                            {t('TableHeader_HandlingTime')}
                        </TableCellLayout>
                    </Tooltip>
                );
            },
            compare: (a, b) => a.elapsedMs - b.elapsedMs,
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'lastMessage',
            renderCell: (item) => (
                <Tooltip
                    content={getLastMessageTooltip(item)}
                    relationship="label"
                >
                    <TableCellLayout
                        className={getAwaitingGroundResponseClass(item)}
                    >
                        {item.latestMessageTime}
                    </TableCellLayout>
                </Tooltip>
            ),
            renderHeaderCell: () => (
                <Tooltip
                    content={t(
                        'Tooltip_LastMessage',
                        'Time of the most recent message from either team'
                    )}
                    relationship="label"
                >
                    <TableCellLayout media={<Chat16Regular />}>
                        {t('TableHeader_LastMessage', 'Last Message')}
                    </TableCellLayout>
                </Tooltip>
            ),
            compare: (a, b) =>
                safeDateCompare(
                    a.latestMessageTimeRaw,
                    b.latestMessageTimeRaw
                ),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'groundTeamOwner',
            renderCell: (item) => {
                const isOwnedByCurrentUser =
                    item.groundTeamOwnerId === currentUserId;
                const showClaimButton =
                    !item.isAutoConnection &&
                    (!item.groundTeamOwnerId || !isOwnedByCurrentUser);

                return (
                    <TableCellLayout
                        className={getAwaitingGroundResponseClass(item)}
                    >
                        {item.groundTeamOwner}
                        {showClaimButton && (
                            <Button
                                className={styles.claimConversationBtn}
                                aria-label={t('Button_ClaimConversation')}
                                appearance="secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClaimConversationClick(
                                        item.conversationId,
                                        item.groundTeamOwnerId
                                    );
                                }}
                            >
                                {t('Button_ClaimConversation')}
                            </Button>
                        )}
                    </TableCellLayout>
                );
            },
            renderHeaderCell: () => (
                <TableCellLayout media={<Person16Regular />}>
                    {t('TableHeader_GroundTeamOwner') || 'Owner'}
                </TableCellLayout>
            ),
            compare: (a, b) =>
                safeLocaleCompare(a.groundTeamOwner, b.groundTeamOwner),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'read',
            renderCell: (item) => (
                <TableCellLayout>
                    {item.readAtRaw ? (
                        <Tooltip
                            content={`${t('Tooltip_Read')} ${formatCompactDateTime(item.readAtRaw)}`}
                            relationship="label"
                        >
                            <Eye16Regular className={styles.eyeIconRead} />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            content={t('Tooltip_Unread') || 'Unread'}
                            relationship="label"
                        >
                            <span className={styles.eyeIconUnread}>
                                <EyeOff16Regular />
                            </span>
                        </Tooltip>
                    )}
                </TableCellLayout>
            ),
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<Eye16Regular />}>
                        {t('TableHeader_ReadStatus')}
                    </TableCellLayout>
                );
            },
            compare: (a, b) =>
                safeDateCompare(
                    a.readAtRaw || undefined,
                    b.readAtRaw || undefined
                ),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'arrivalTime',
            renderCell: (item) => (
                <TableCellLayout
                    className={mergeClasses(
                        getAwaitingGroundResponseClass(item),
                        item.isArrivingSoon ? styles.arrivingSoonField : ''
                    )}
                >
                    {formatCompactDateTime(item.arrivalTime)}
                </TableCellLayout>
            ),
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<CalendarClock16Regular />}>
                        {t('TableHeader_ArrivalTime')}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => safeDateCompare(a.arrivalTime, b.arrivalTime),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'flight',
            renderCell: (item) => (
                <TableCellLayout
                    className={getAwaitingGroundResponseClass(item)}
                >
                    {item.flight}
                </TableCellLayout>
            ),
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<NumberRow16Regular />}>
                        {t('TableHeader_Flight')}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => safeFlightCompare(a.flight, b.flight),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'route',
            renderCell: (item) => (
                <TableCellLayout
                    className={getAwaitingGroundResponseClass(item)}
                >
                    {item.route}
                </TableCellLayout>
            ),
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<ArrowRoutingRegular />}>
                        {t('TableHeader_Airport')}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => safeLocaleCompare(a.route, b.route),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'acReg',
            renderCell: (item) => (
                <TableCellLayout
                    className={getAwaitingGroundResponseClass(item)}
                >
                    {item.acReg}
                </TableCellLayout>
            ),
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<Airplane16Regular />}>
                        {t('TableHeader_AircraftReg')}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => safeLocaleCompare(a.acReg, b.acReg),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'initiatedFrom',
            renderCell: (item) => (
                <TableCellLayout
                    className={getAwaitingGroundResponseClass(item)}
                >
                    {item.initiatedFrom}
                    {(item.isAutoConnection || item.isAutoGeneratedConversation) && (
                        <Tooltip
                            content={
                                item.isAutoConnection
                                    ? 'Auto connection: created automatically; no conversation has started yet.'
                                    : 'Auto-generated conversation: created from an automatic connection; cannot be resolved.'
                            }
                            relationship="label"
                        >
                            <Badge className={styles.autoBadge}>AUTO</Badge>
                        </Tooltip>
                    )}
                </TableCellLayout>
            ),
            renderHeaderCell: () => (
                <TableCellLayout media={<People16Regular />}>
                    {t('TableHeader_InitiatedFrom') || 'Initiated From'}
                </TableCellLayout>
            ),
            compare: (a, b) =>
                safeLocaleCompare(a.initiatedFrom, b.initiatedFrom),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'status',
            renderCell: (item) => {
                const colorHex = item.conversationColor;
                const indicatorStyle = colorHex
                    ? { backgroundColor: `#${colorHex}` }
                    : { backgroundColor: '#808080' };

                return (
                    <TableCellLayout
                        className={getAwaitingGroundResponseClass(item)}
                    >
                        <div className={styles.statusWrapper}>
                            <div
                                className={styles.conversationStatusIndicator}
                                style={indicatorStyle}
                            />
                            {item.conversationStatus}
                        </div>
                    </TableCellLayout>
                );
            },
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<Status16Regular />}>
                        {t('TableHeader_ConversationStatus')}
                    </TableCellLayout>
                );
            },
            compare: (a, b) =>
                safeLocaleCompare(a.conversationStatus, b.conversationStatus),
        }),
        createTableColumn<ActiveConversationRow>({
            columnId: 'categories',
            renderCell: (item) => (
                <Tooltip
                    content={item.categories}
                    relationship="description"
                    positioning="below-start"
                >
                    <TableCellLayout
                        className={mergeClasses(
                            styles.categoriesCell,
                            getAwaitingGroundResponseClass(item)
                        )}
                    >
                        {item.categories}
                    </TableCellLayout>
                </Tooltip>
            ),
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<Tag16Regular />}>
                        {t('TableHeader_Categories')}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => safeLocaleCompare(a.categories, b.categories),
        }),
    ];
};
